<template>
  <div class="chat-data-wrap">
    <ul class="chat-data-list" ref="messageBody" @scroll="handleMessagesScroll">
      <li
        class="text-align-center color-gray"
        v-if="currentPageMessages.length === 0 && messages.length !== 0"
      >
        <h4 class="bold extra-info">No older messages</h4>
      </li>
      <li
        :class="{
          sender: message.msgFrom === profileId,
          receiver: message.msgTo === profileId
        }"
        v-for="(message, index) in formattedMessages"
        :key="index"
        :ref="message.id"
      >
        <div
          class="day-divider"
          v-if="
            new Date(message.createdAt).toDateString() !==
              (messages[index - 1] &&
                new Date(messages[index - 1]['createdAt']).toDateString())
          "
        >
          <span>
            {{ message.createdAt | humanizeMessageDate }}
          </span>
        </div>
        <div class="chat-content">
          <div class="user-avatar" v-if="message.msgTo === profileId">
            <img
              :src="generateImageUrl(formattedReceiverInfo.imageUrl)"
              alt="profile-pic"
            />
          </div>
          <div class="message-detail-wrap">
            <div class="message-detail">
              <div
                class="message-text"
                v-html="message.chatMessage.replaceAll(`\n`, '<br />')"
              ></div>
              <div class="message-time">
                {{ message.createdAt | dateToTime }}
              </div>
            </div>
            <div
              class="seen-info"
              v-if="
                message.msgFrom === profileId &&
                  selectedConversation.allMessegeRead &&
                  index === formattedMessages.length - 1
              "
            >
              <div class="icon-wrap">
                <img src="@/assets/icons/seen.svg" alt="seen" />
              </div>
              Seen
            </div>
          </div>
        </div>
      </li>
      <li
        class="text-align-center color-gray"
        v-if="blocked || deactivated"
      >
        <h4 class="bold extra-info">
          The user is no longer available
        </h4>
      </li>
      <li v-else-if="(basicPlayer && !senderIsClubCoach)">
        <h4 class="bold extra-info">
          This user profile has been downgraded  and user is no longer available
        </h4>
      </li>
      <li
        class="text-align-center color-gray"
        v-else-if="senderIsBasicPlayer && !receiverIsClubCoach"
      >
        <h4 class="bold extra-info">
          You are using a basic profile. Upgrade your profile to chat with this
          coach.
        </h4>
      </li>
      <li
        class="text-align-center color-gray"
        v-else-if="inactiveUsersShown && !(blocked || deactivated)"
      >
        <h4 class="bold extra-info">
          If you reply to this user, the contact will be moved to Active
          Contacts List
        </h4>
      </li>
      <li class="typing" v-if="typing">
        <div class="user-avatar">
          <img
            :src="generateImageUrl(formattedReceiverInfo.imageUrl)"
            alt="profile-pic"
          />
        </div>
        <div class="typing-animation">
          <span>Typing</span>
          <div class="loader"></div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Chat, Message } from "@/models";

const STORE_KEY = "chatStore";

export default {
  name: "ConvoBody",
  computed: {
    profileId() {
      return Chat.profileId();
    },
    ...mapGetters({
      currentPageMessages: `${STORE_KEY}/currentPageMessages`,
      currentPage: `${STORE_KEY}/currentPage`,
      receiverInfo: `${STORE_KEY}/receiverProfile`
    }),
    formattedReceiverInfo() {
      return Chat.formatReceiverInfo(this.receiverInfo);
    },
    formattedMessages() {
      let messages = this.messages;
      if (this.searchQuery) {
        messages = messages.map(
          message =>
            new Message({
              ...message,
              chatMessage: message.chatMessage.replaceAll(
                this.searchQuery,
                `<span class="marked">${this.searchQuery}</span>`
              )
            })
        );
      }
      const urls = messages
        .map(message => [
          ...message.chatMessage.matchAll(
            /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/g
          )
        ])
        .map(url => (url.length === 0 ? [] : [...url[0].filter(u => u)]));
      messages = messages.map((message, index) => {
        let chatMessage = message.chatMessage;
        const messageUrls = Array.from(new Set(urls[index]));
        messageUrls.forEach(url => {
          const urlCopy = url;
          chatMessage = chatMessage.replaceAll(
            url,
            `<a href="${urlCopy}" target="_blank">${urlCopy}</a>`
          );
        });
        return new Message({ ...message, chatMessage });
      });
      return messages;
    }
  },
  props: {
    messages: {
      type: Array,
      default: () => []
    },
    typing: {
      type: Boolean,
      default: false
    },
    scroll: {
      type: Number,
      required: true
    },
    forceScroll: {
      type: Number,
      required: true
    },
    searchedMessage: {
      type: Object,
      default: null
    },
    deactivated: {
      type: Boolean,
      default: false
    },
    blocked: {
      type: Boolean,
      default: false
    },
    latestMessageRead: {
      type: Boolean,
      default: false
    },
    latestMessageId: {
      type: String,
      default: null
    },
    basicPlayer: {
      type: Boolean,
      default: false
    },
    senderIsBasicPlayer: {
      type: Boolean,
      default: false
    },
    receiverIsClubCoach: {
      type: Boolean,
      default: false
    },
    senderIsClubCoach: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ""
    },
    selectedConversation: {
      type: Object,
      default: () => ({})
    },
    inactiveUsersShown: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleMessagesScroll(event) {
      if (event.target.scrollTop === 0) {
        this.$emit("scroll-top");
      }
      if (
        event.target.scrollTop + event.target.clientHeight >=
        event.target.scrollHeight
      ) {
        this.$emit("scroll-bottom");
      }
    }
  },
  watch: {
    scroll() {
      let scrollToBottom = false;
      if (this.$refs.messageBody) {
        if (
          this.$refs.messageBody.scrollHeight -
            this.$refs.messageBody.scrollTop -
            this.$refs.messageBody.clientHeight <=
          300
        )
          scrollToBottom = true;
      }
      if (this.messages[this.messages.length - 1].msgFrom === this.profileId)
        scrollToBottom = true;
      if (scrollToBottom)
        this.$refs.messageBody &&
          this.$refs.messageBody.scroll({
            left: 0,
            top: this.$refs.messageBody.scrollHeight,
            behavior: "smooth"
          });
    },
    forceScroll() {
      if (!this.$refs.messageBody) return;

      this.$refs.messageBody.style.overflow = "hidden";
      this.$refs.messageBody.scroll({
        left: 0,
        top: this.$refs.messageBody.scrollHeight,
        behavior: "smooth"
      });
      this.$refs.messageBody.style.overflow = "auto";
    },
    searchedMessage(newVal) {
      this.$refs[newVal.id][0].scrollIntoView({ behavior: "smooth" });
    }
  }
};
</script>

<style lang="scss">
.marked {
  background-color: yellow;
}
.chat-data-wrap {
  width: 100%;
  position: absolute;
  .chat-data-list {
    margin: 0;
    padding: 20px !important;
    display: flex;
    flex-direction: column;
    height: calc(100vh - 230px);
    min-height: 373px;
    overflow-y: scroll;
    .text-align-center {
      text-align: center;
    }
    .color-gray {
      color: #8a8d91;
    }
    .bold {
      font-weight: 500 !important;
    }
    li {
      width: 100%;
      padding: 10px 0 30px 0;
      .day-divider {
        width: 100%;
        text-align: center;
        position: relative;
        padding: 10px 0;
        margin-bottom: 20px;
        span {
          font-size: 12px;
          color: #8a8d91;
          background: #ffffff;
          position: relative;
          z-index: 2;
          padding: 0 8px;
        }
        &::before {
          content: "";
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate3d(-50%, -50%, 0);
          width: 100%;
          height: 1px;
          background: #d6d6d6;
          border-radius: 50px;
          z-index: 1;
        }
      }
      .chat-content {
        display: flex;
        max-width: 700px;
        .user-avatar {
          width: 32px;
          height: 32px;
          margin-right: 10px;
          position: relative;
          top: 5px;
        }
        .message-detail-wrap {
          width: fit-content;
          max-width: 70%;
        }
        .message-detail {
          padding: 10px 10px 28px 15px;
          background: #f1f1f4;
          border-radius: 14px;
          position: relative;
          .message-text {
            color: #303030;
            font-size: 13px;
            line-height: 23px;
            font-weight: 400;
            padding-right: 40px;
            overflow-wrap: break-word;
          }
          .message-time {
            position: absolute;
            right: 14px;
            bottom: 10px;
            font-size: 10px;
            color: rgba(0, 0, 0, 0.8);
          }
        }
        .seen-info {
          text-align: right;
          padding-top: 10px;
          padding-right: 20px;
          color: #303030;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          .icon-wrap {
            width: 15px;
            margin-right: 3px;
          }
        }
      }
      &.sender {
        .chat-content {
          justify-content: flex-end;
          max-width: 700px;
          .message-detail {
            background: #e8faff;
          }
        }
      }
      &.typing {
        position: relative;
        .user-avatar {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translate3d(0, -50%, 0);
          width: 25px;
          height: 25px;
        }
      }
    }
    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #cacacc;
      outline: none;
    }
  }
  .typing-animation {
    display: flex;
    padding-left: 35px;
    span {
      font-size: 14px;
      color: #8f8f8f;
      font-weight: 400;
      font-style: italic;
    }
    .loader,
    .loader:before,
    .loader:after {
      border-radius: 50%;
      width: 6px;
      height: 6px;
      -webkit-animation-fill-mode: both;
      animation-fill-mode: both;
      -webkit-animation: load7 1.6s infinite ease-in-out;
      animation: load7 1.6s infinite ease-in-out;
    }
    .loader {
      color: #8ca4b7;
      font-size: 12px;
      margin: 0px;
      top: -21px;
      left: 20px;
      position: relative;
      -webkit-transform: translateZ(0);
      -ms-transform: translateZ(0);
      transform: translateZ(0);
      -webkit-animation-delay: -0.16s;
      animation-delay: -0.16s;
    }
    .loader:before,
    .loader:after {
      content: "";
      position: absolute;
      top: 0;
    }
    .loader:before {
      color: #b0bfcc;
      left: -10px;
      -webkit-animation-delay: -0.32s;
      animation-delay: -0.32s;
    }
    .loader:after {
      left: 10px;
      color: #7091a7;
    }
  }
  @-webkit-keyframes load7 {
    0%,
    80%,
    100% {
      box-shadow: 0 2.5em 0 -1.3em;
    }
    40% {
      box-shadow: 0 2.5em 0 0;
    }
  }
  @keyframes load7 {
    0%,
    80%,
    100% {
      box-shadow: 0 2.5em 0 -1.3em;
    }
    40% {
      box-shadow: 0 2.5em 0 0;
    }
  }
}
@media only screen and (max-width: 575px) {
  .chat-data-wrap {
    .extra-info {
      font-size: 14px;
      line-height: 22px !important;
    }
    .chat-data-list {
      li {
        .chat-content {
          .message-detail-wrap {
            max-width: 100%;
          }
        }
      }
    }
  }
}
</style>
